class PredictiveSearch extends Theme.Component {
  constructor() {
    super();

    this.modal = this.querySelector('[data-predictive-search-modal]');
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearch = this.querySelector('[data-predictive-search]');
    this.defaultContent = this.querySelector('[data-predictive-search-default]');
    this.cachedResults = {};
  }

  connectedCallback() {
    // Query after upgrade so all instances are in the DOM
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');

    document.addEventListener("searchlink:click", () => this.show());
    document.addEventListener("predictivesearch:close", () => this.hide());

    const closeButton = this.querySelector('[data-predictive-modal-close]');
    if (closeButton) {
      closeButton.addEventListener("click", () => this.hide());
    }

    const clearButton = this.querySelector('button[data-predictive-search-clear]');
    if (clearButton && this.input) {
      clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.searchTerm = '';
        if (this.predictiveSearch) this.predictiveSearch.innerHTML = '';
        this._showDefault();
        this.input.focus();
      });
    }

    if (this.input) {
      this.input.addEventListener('input', this._debounce(this.onChange.bind(this), 300));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
    }

    // Close when clicking outside the modal
    document.addEventListener('click', (e) => {
      if (this.modal?.classList.contains('visible') && !this.contains(e.target)) {
        this.hide();
      }
    });

    // Close when focus leaves via keyboard (Tab away)
    this.addEventListener('focusout', this.onFocusOut.bind(this));
  }

  onChange(event) {
    const newSearchTerm = event.target.value.trim();
    this.searchTerm = newSearchTerm;

    if (this.searchTerm.length) {
      this.getSearchResults(this.searchTerm);
    } else {
      if (this.predictiveSearch) this.predictiveSearch.innerHTML = '';
      this._showDefault();
    }
  }

  getSearchResults(query) {
    const queryKey = query.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const url = `/search/suggest?q=${encodeURIComponent(query)}&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search`;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Fetch failed with status ${res.status}`);
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const results = doc.querySelector('#shopify-section-predictive-search');
        if (!results) {
          console.error('Predictive search: #shopify-section-predictive-search not found in response');
          return;
        }
        document.querySelectorAll('predictive-search').forEach((instance) => {
          instance.cachedResults[queryKey] = results.innerHTML;
        });
        this.renderSearchResults(results.innerHTML);
      })
      .catch((err) => {
        console.error('Predictive search fetch failed:', err);
        this._hideDefault();
        if (this.predictiveSearch) {
          this.predictiveSearch.innerHTML = `
            <div class="predictive-search-results">
              <p class="font-2 size--1 inline-2 block-1">
                Something went wrong. <a href="/search?q=${encodeURIComponent(query)}&type=product">See all results for "${query}"</a>
              </p>
            </div>`;
        }
      });
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearch) {
      console.error('Predictive search: results container element not found');
      return;
    }
    this._hideDefault();
    this.predictiveSearch.innerHTML = resultsMarkup;
    if (window.alignProductCardsHeight) alignProductCardsHeight();

    const allResultsButton = this.querySelector('[data-predictive-all-results]');
    if (allResultsButton && this.input) {
      allResultsButton.addEventListener('click', () => {
        const query = this.input.value.trim();
        if (query.length) {
          window.location.href = `/search?q=${encodeURIComponent(query)}&type=product`;
        }
      });
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.hide();
      this.input.blur();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();

    const activeContainer = this.defaultContent?.classList.contains('state--visible')
      ? '[data-predictive-search-default] a, [data-predictive-search-default] button:not([disabled])'
      : '[data-predictive-search] a, [data-predictive-search] button:not([disabled])';

    const focusable = [...this.querySelectorAll(activeContainer)];
    if (!focusable.length) return;

    const currentIndex = focusable.indexOf(document.activeElement);

    let nextIndex;
    if (event.key === 'ArrowDown') {
      nextIndex = currentIndex < focusable.length - 1 ? currentIndex + 1 : 0;
    } else {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : focusable.length - 1;
    }

    focusable[nextIndex]?.focus();
  }

  _debounce(fn, delay) {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  show() {
    if (this.modal) {
      this.modal.classList.add("visible");
      document.body.classList.add("no-scroll");
      if (this.input) this.input.focus();
      if (!this.input?.value.trim()) {
        this._showDefault();
      }
    }
  }

  hide() {
    if (this.modal) {
      this.modal.classList.remove("visible");
      document.body.classList.remove("no-scroll");
      this._hideDefault();
    }
  }

  _showDefault() {
    if (this.defaultContent) this.defaultContent.classList.add('state--visible');
  }

  _hideDefault() {
    if (this.defaultContent) this.defaultContent.classList.remove('state--visible');
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(text) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = text;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  onFocusOut(event) {
    // Mouse clicks are handled by the document click listener above.
    // This only needs to handle keyboard Tab away from the modal.
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && !this.contains(relatedTarget)) {
      this.hide();
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);
